import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';

const TYPE_CONFIG = {
  NATIONAL:  { className: 'bg-blue-500' },
  REGIONAL:  { className: 'bg-purple-500' },
  RELIGIOUS: { className: 'bg-green-500' },
  COMPANY:   { className: 'bg-orange-500' },
  OPTIONAL:  { className: 'bg-gray-500' },
};

const getTypeBadge = (type, t) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.NATIONAL;
  return <Badge className={config.className}>{t(`holidayCommon.types.${type}`, { defaultValue: type })}</Badge>;
};

const formatDate = (dateString, language) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(language, { day: '2-digit', month: 'long', year: 'numeric' });
};

const CalendrierDetailAddHolidayDialog = ({
  isOpen, onClose,
  calendarName, calendarYear,
  availableHolidays,
  selectedHolidaysToAdd, setSelectedHolidaysToAdd,
  handleAddHolidays,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t("holidayCalendarComponents.addHolidayDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("holidayCalendarComponents.addHolidayDialog.desc", { name: calendarName })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {availableHolidays.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2">{t("holidayCalendarComponents.addHolidayDialog.noneForYear", { year: calendarYear })}</p>
            <p className="text-sm mt-1">{t("holidayCalendarComponents.addHolidayDialog.allAlreadyAdded")}</p>
          </div>
        ) : (
          <div className="border rounded-md p-3 max-h-96 overflow-y-auto space-y-2">
            {availableHolidays.map((holiday) => (
              <div key={holiday.id} className="flex items-start space-x-2 p-2 hover:bg-accent rounded">
                <Checkbox
                  id={`add-holiday-${holiday.id}`}
                  checked={selectedHolidaysToAdd.includes(holiday.id)}
                  onCheckedChange={(checked) => {
                    setSelectedHolidaysToAdd(checked
                      ? [...selectedHolidaysToAdd, holiday.id]
                      : selectedHolidaysToAdd.filter((id) => id !== holiday.id)
                    );
                  }}
                />
                <Label htmlFor={`add-holiday-${holiday.id}`} className="cursor-pointer flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{holiday.name}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(holiday.date, i18n.language)}</p>
                    </div>
                    {getTypeBadge(holiday.type, t)}
                  </div>
                </Label>
              </div>
            ))}
          </div>
        )}

        {selectedHolidaysToAdd.length > 0 && (
          <p className="text-sm text-muted-foreground">
            {t("holidayCalendarComponents.form.selectedHolidays", { count: selectedHolidaysToAdd.length })}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => { onClose(false); setSelectedHolidaysToAdd([]); }}>
          {t("holidayCommon.cancel")}
        </Button>
        <Button onClick={handleAddHolidays} disabled={selectedHolidaysToAdd.length === 0}>
          {t("holidayCalendarComponents.addHolidayDialog.add")} {selectedHolidaysToAdd.length > 0 && `(${selectedHolidaysToAdd.length})`}
        </Button>
      </DialogFooter>
    </DialogContent>
    </Dialog>
  );
};

export default CalendrierDetailAddHolidayDialog;
